import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../information.module.scss";
import GaTextInput from "../../../../../../../../components/elements/forms/formFields/textInput/GaTextInput";
import GaSelect from "../../../../../../../../components/elements/forms/formFields/select/GaSelect";
import { countriesAndStates } from "../../../../../../../../data/forms";
import cx from "classnames";
import { ILegalAddress } from "../../../../../../../../interfaces/interfaces";
import { initialLang } from "../../../../../../../../i18n";
import LightPanel from "../../../../../../../../components/elements/panels/LightPanel/LightPanel";
import PurpleButton from "../../../../../../../../components/atoms/buttons/purple/PurpleButton";
import GreyButton from "../../../../../../../../components/atoms/buttons/grey/GreyButton";
import { ButtonSizes, TextSizes } from "../../../../../../../../data/display";
import CloseIcon from "../../../../../../../../assets/iconsLibrary/simple/close/closeIcon";
import { useSelector } from "react-redux";
import { clientsSelectors } from "../../../../../../store";
import { ActionStatusTypes } from "../../../../../../../../data/common";

export type InformationFormProps = {
  formData: ILegalAddress;
  orgAddress?: ILegalAddress;
  secondFormActive: boolean;
  secondFormDataModified: boolean;
  states: string[];
  orgCountry: any;
  defaultStates: string[];
  emptySecondFormData: ILegalAddress;
  setLegalAddressState: (x?: any) => void;
  setSecondFormActive: (x: boolean) => void;
  setFormState: (x: any) => void;
  setStates: (x?: any) => void;
  getStates: (x?: any) => void;
  handleAddressInputsChange: (x: any, y?: any) => void;
  updateWithSecondFormData: (x?: any) => void;
};

const InformationForm: React.FC<InformationFormProps> = (props) => {
  const {
    states,
    formData,
    orgAddress,
    secondFormActive,
    orgCountry,
    defaultStates,
    emptySecondFormData,
    secondFormDataModified,
    getStates,
    setLegalAddressState,
    setStates,
    setSecondFormActive,
    handleAddressInputsChange,
    updateWithSecondFormData,
  } = props;

  const { t } = useTranslation();
  const { street, suite, number, locality, region, country, postalCode } =
    formData;

  const updateOrgStatus = useSelector(clientsSelectors.getOrgByIdStatus);

  useEffect(() => {
    if (updateOrgStatus === ActionStatusTypes?.success && secondFormActive) {
      setSecondFormActive(false);
    } else if (
      updateOrgStatus === ActionStatusTypes?.failed &&
      secondFormActive
    ) {
      setLegalAddressState(emptySecondFormData);
    }
  });

  return (
    <LightPanel className={styles.formLightPanel}>
      <div className={styles.formLightPanel__header}>
        <p className={cx("heading5 neutral1000")}>
          {t("clientDetail.companyInformation")}
        </p>
        {!secondFormActive ? (
          <GreyButton
            size={ButtonSizes.SM}
            label={t("general.edit")}
            outlined
            action={() => setSecondFormActive(true)}
          />
        ) : (
          <div
            onClick={() => (
              setLegalAddressState(emptySecondFormData),
              setSecondFormActive(false),
              setStates(orgCountry?.states || defaultStates)
            )}>
            <CloseIcon size={24} className={styles.closeFormIcon} />
          </div>
        )}
      </div>
      <div className={styles.form}>
        <div className={styles.form__textInputs}>
          <GaTextInput
            name="street"
            readOnly={!secondFormActive}
            invalid={false}
            labelText={t("clientDetail.address")}
            onChange={(e) => handleAddressInputsChange(e)}
            placeholder={t("clientDetail.addressPlaceholder")}
            value={street}
            textSize={TextSizes.MD}
          />
          <div className={styles.form__textInputs__doubleColumn}>
            <GaTextInput
              name="number"
              invalid={false}
              readOnly={!secondFormActive}
              labelText={t("welcome.number")}
              onChange={(e) => handleAddressInputsChange(e)}
              placeholder={t("welcome.number")}
              value={number}
              textSize={TextSizes.MD}
            />
            <GaTextInput
              name="suite"
              invalid={false}
              readOnly={!secondFormActive}
              labelText={t("welcome.suite")}
              onChange={(e) => handleAddressInputsChange(e)}
              placeholder={t("welcome.suitePlaceholder")}
              type="text"
              value={suite}
              textSize={TextSizes.MD}
            />
          </div>
          <div className={styles.form__textInputs__doubleColumn}>
            <GaSelect
              idSelect={"country"}
              value={country}
              name="country"
              readOnly={!secondFormActive}
              isLoading={!countriesAndStates?.length}
              labelText={t("welcome.country")}
              optionValueProperty={
                initialLang ? "name_" + initialLang : "name_es"
              }
              optionTextProperty={
                initialLang ? "name_" + initialLang : "name_es"
              }
              onChange={(e) => {
                handleAddressInputsChange(e), getStates(e.target.value);
              }}
              options={countriesAndStates}
              textSize={TextSizes.MD}
            />

            <GaSelect
              idSelect={"regio"}
              value={region}
              name="region"
              readOnly={!secondFormActive}
              isLoading={!states?.length}
              labelText={t("welcome.region")}
              onChange={(e) => handleAddressInputsChange(e)}
              options={states}
              textSize={TextSizes.MD}
            />
          </div>
          <div className={styles.form__textInputs__doubleColumn}>
            <GaTextInput
              name="locality"
              invalid={false}
              readOnly={!secondFormActive}
              labelText={t("welcome.locality")}
              onChange={(e) => handleAddressInputsChange(e)}
              placeholder={t("welcome.locality")}
              type="text"
              value={locality}
              textSize={TextSizes.MD}
            />

            <GaTextInput
              name="postalCode"
              invalid={false}
              readOnly={!secondFormActive}
              labelText={t("welcome.postalCode")}
              onChange={(e) => handleAddressInputsChange(e)}
              placeholder={t("welcome.postalCode")}
              type="text"
              value={postalCode}
              textSize={TextSizes.MD}
            />
          </div>
        </div>
      </div>
      {secondFormActive ? (
        <div className={styles.formButtons}>
          <GreyButton
            size={ButtonSizes.SM}
            label={t("general.cancel")}
            outlined
            action={() => setSecondFormActive(false)}
          />{" "}
          <PurpleButton
            size={ButtonSizes.SM}
            label={t("general.save")}
            action={() => updateWithSecondFormData()}
            disabled={!secondFormDataModified}
          />
        </div>
      ) : null}
    </LightPanel>
  );
};

export default InformationForm;
